import type {
  AdminUser,
  CheckoutRequest,
  CheckoutResponse,
  Category,
  Order,
  Product,
} from "@shared/types";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  categories: {
    list: () => request<Category[]>("/categories"),
  },
  products: {
    list: (params?: { category?: string; q?: string }) => {
      const qs = new URLSearchParams();
      if (params?.category) qs.set("category", params.category);
      if (params?.q) qs.set("q", params.q);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return request<Product[]>(`/products${suffix}`);
    },
    get: (slug: string) => request<Product>(`/products/${slug}`),
  },
  checkout: {
    submit: (payload: CheckoutRequest) =>
      request<CheckoutResponse>("/checkout", { method: "POST", body: JSON.stringify(payload) }),
  },
  orders: {
    get: (id: string) => request<Order>(`/orders/${id}`),
  },
  auth: {
    me: () => request<AdminUser>("/auth/me"),
    logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
    googleLoginUrl: () => "/api/auth/google/login",
  },
  admin: {
    products: {
      list: () => request<Product[]>("/admin/products"),
      create: (payload: Partial<Product> & { name: string; priceCents: number }) =>
        request<Product>("/admin/products", { method: "POST", body: JSON.stringify(payload) }),
      update: (id: string, payload: Partial<Product>) =>
        request<Product>(`/admin/products/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
      remove: (id: string) => request<{ ok: true }>(`/admin/products/${id}`, { method: "DELETE" }),
      uploadImage: async (id: string, file: File, alt?: string) => {
        const form = new FormData();
        form.append("file", file);
        if (alt) form.append("alt", alt);
        const res = await fetch(`/api/admin/products/${id}/images`, {
          method: "POST",
          credentials: "include",
          body: form,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: res.statusText }));
          throw new ApiError(res.status, body.error ?? res.statusText);
        }
        return res.json();
      },
      removeImage: (id: string, imageId: string) =>
        request<{ ok: true }>(`/admin/products/${id}/images/${imageId}`, { method: "DELETE" }),
    },
    orders: {
      list: (status?: string) => request<Order[]>(`/admin/orders${status ? `?status=${status}` : ""}`),
      get: (id: string) => request<Order>(`/admin/orders/${id}`),
      update: (id: string, payload: { status?: Order["status"]; paymentStatus?: Order["paymentStatus"] }) =>
        request<Order>(`/admin/orders/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
    },
    categories: {
      list: () => request<Category[]>("/admin/categories"),
      create: (payload: { name: string; slug?: string }) =>
        request<Category>("/admin/categories", { method: "POST", body: JSON.stringify(payload) }),
      update: (id: string, payload: { name?: string; slug?: string; sortOrder?: number }) =>
        request<Category>(`/admin/categories/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
      remove: (id: string) => request<{ ok: true }>(`/admin/categories/${id}`, { method: "DELETE" }),
    },
  },
};

export { ApiError };
